import React from 'react';
import { joinClasses } from '../../../../Utils/className.util';
// @ts-ignore
import styles from './CentralEvidenceVirtualProfileSelection.module.scss';
import { selectUserAllowedWorkplaces } from '../../../../store/reducers/userSlice';
import { useSelector } from 'react-redux';
import apiClient from '../../../../Services/ApiClient';
import { useAppContext } from '../../../../Services/AppContext';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import Text from '../../../../Components/Base/Text/Text';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';

export interface WorkplacePatient {
	id: string;
	workplace: {
		id: string;
		name: string;
	};
	user: {
		id: string;
	};
}

interface Props {
	onSelect: (patientId: string, selectedWorkplaceId: string) => void;
	patientWorkplaces: WorkplacePatient[];
	originalId: string;
}

const CentralEvidenceVirtualProfileSelection: React.FC<Props> = ({
	onSelect,
	patientWorkplaces,
	originalId,
}) => {
	const { workplaceId } = useAppContext();
	const allowedWorkplaces: { id: string; name: string }[] = useSelector(
		selectUserAllowedWorkplaces,
	);
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { displayError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();

	const handleAddPatientToActiveWorkplace = async () => {
		try {
			const result = await apiClient.post(
				`/workplaces/${workplaceId}/patient/connect/${originalId}`,
				{},
			);
			if (result?.status === 200) {
				showNotificationSuccess({
					content: t(
						'component.centralEvidenceVirtualProfileSelection.patientAddedToWorkplace',
					),
				});
				onSelect(result.data?.userId, workplaceId);
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
	};

	return (
		<div className={styles.wrapper}>
			{patientWorkplaces.length > 0 ? (
				<>
					<div className={styles.text}>
						{t(
							'component.centralEvidenceVirtualProfileSelection.patientIsRegisteredAtWorkplaces',
						)}
						:
					</div>
					{patientWorkplaces?.map(item => {
						const isAccessAllowed = allowedWorkplaces.some(
							workplace => workplace.id === item.workplace.id,
						);
						return (
							<div
								className={joinClasses([
									styles.item,
									isAccessAllowed ? '' : styles.accessDenied,
								])}
								key={item.id}
								onClick={() =>
									isAccessAllowed &&
									onSelect(item?.user?.id, item?.workplace?.id)
								}>
								<div className={styles.content}>
									<div className={styles.title}>{item.workplace.name}</div>
									<Text
										className={styles.subtitle}
										tx={
											isAccessAllowed
												? 'component.centralEvidenceVirtualProfileSelection.addToActiveWorkplaceByClick'
												: 'component.centralEvidenceVirtualProfileSelection.youDontHaveAccessToThisWorkplace'
										}
									/>
								</div>
								<img
									src={
										isAccessAllowed
											? '/dist/icons/arrow-right-solid-blue.svg'
											: '/dist/icons/xmark-solid.svg'
									}
								/>
							</div>
						);
					})}
				</>
			) : (
				<Text
					className={styles.textWarning}
					tx='component.centralEvidenceVirtualProfileSelection.patientIsNotInAnyWorkplace'
				/>
			)}
			<Text
				className={styles.text2}
				tx='component.centralEvidenceVirtualProfileSelection.patientIsNotInEArchiveOfActiveWorkplace'
			/>
			<div className={styles.item} onClick={handleAddPatientToActiveWorkplace}>
				<div className={styles.content}>
					<Text
						className={styles.title}
						tx='component.centralEvidenceVirtualProfileSelection.addToEArchiveOfActiveWorkplace'
					/>
					<div className={styles.subtitle}>
						{allowedWorkplaces.find(item => item.id === workplaceId)?.name}
					</div>
				</div>
				<img src='/dist/icons/plus-solid-blue.svg' alt='plus' />
			</div>
		</div>
	);
};

export default CentralEvidenceVirtualProfileSelection;
